import {
  createAction,
  createAsyncThunk,
  createSlice,
  nanoid,
} from '@reduxjs/toolkit'
import { ImageSizes } from 'features/image/ImageSizes'
import api from 'api/axios/foka'
import { getProgressPercent } from 'features/creative/creativeUtil'
import axios from 'axios'
import { createAxiosThunk } from 'utils/reduxUtil'

const initialState = {
  isShow: false,
  pending: false,
  uploading: false,
  loading: false,
  uploadOnly: false,
  allowImageSizes: [
    ImageSizes['450x450'],
    ImageSizes['348x230'],
    ImageSizes['1200x380'],
  ],
  maxCount: 100,
  progress: {},
  progressCount: 0,
  totalProgress: 0,
  pendingImages: [],
  images: [],
  selectedImages: [],
  libraryImages: [],
  tabIndex: 0,
  name: '',
  page: 0,
  size: 20,
  totalElements: 0,
}

export const fetchModalImageList = createAxiosThunk(
  'image/fetchModalImageList',
  (noArgs, { getState }) => {
    const { allowImageSizes, name, page, size, tabIndex } =
      getState().image.imageLibrary
    return api.image.searchDspImages({
      name,
      isRecentlyUsed: tabIndex === 2,
      sizeTypes: allowImageSizes,
      page,
      size,
    })
  }
)

const uploadProgress = createAction('image/uploadProgress')

export const uploadImage = createAsyncThunk(
  'image/uploadImage',
  async ({ file, index, filesNum, signal }, { dispatch, rejectWithValue }) => {
    try {
      const source = axios.CancelToken.source()
      signal.addEventListener('abort', () => {
        source.cancel()
      })
      const response = await api.image.uploadImageFile(
        file,
        progressEvent => {
          dispatch(
            uploadProgress({
              id: file.name,
              progress: getProgressPercent(progressEvent),
              filesNum,
            })
          )
        },
        source.token
      )
      return {
        ...response.data,
        property: {
          ...response.data.property,
          width: file.width,
          height: file.height,
        },
      }
    } catch (e) {
      if (axios.isCancel(e)) {
        return rejectWithValue({ userCanceled: true })
      }
      return rejectWithValue(e.response.data)
    }
  }
)

export const uploadImages = createAsyncThunk(
  'image/uploadImages',
  async (files, { dispatch, rejectWithValue, signal }) => {
    try {
      await Promise.all(
        files.map((file, index) =>
          dispatch(
            uploadImage({
              file,
              index,
              filesNum: files.length,
              signal,
            })
          )
        )
      )
    } catch (e) {
      if (axios.isCancel(e)) {
        return rejectWithValue({ userCanceled: true })
      }
      return rejectWithValue(e.response.data)
    }
  }
)

export const addImageLibrary = createAxiosThunk(
  'image/addImageLibrary',
  (noArgs, { getState }) => {
    const { selectedImages } = getState().image.imageLibrary
    return api.image.postDspImages({
      commands: selectedImages.map(({ url, width, height, name, size }) => ({
        url,
        width,
        height,
        name,
        size,
      })),
    })
  }
)

const imageLibrarySlice = createSlice({
  name: 'image/imageLibrary',
  initialState,

  reducers: {
    showImageUploadModal: state => {
      state.allowImageSizes = initialState.allowImageSizes
      state.maxCount = initialState.maxCount
      state.images = []
      state.selectedImages = []
      state.tabIndex = 0
      state.name = ''
      state.uploadOnly = true
      state.isShow = true
    },
    showImageLibraryModal: (
      state,
      { payload: { maxCount, allowImageSizes } }
    ) => {
      state.maxCount = maxCount
      state.allowImageSizes = allowImageSizes
      state.images = []
      state.selectedImages = []
      state.tabIndex = 0
      state.name = ''
      state.uploadOnly = false
      state.isShow = true
    },
    hideImageLibraryModal: () => initialState,
    setSelectedImages: (state, { payload }) => {
      const index = state.selectedImages.findIndex(
        ({ id }) => id === payload.id
      )
      if (index !== -1) {
        state.selectedImages = []
      } else {
        state.selectedImages = [payload]
      }
    },
    clearSelectedImages: state => {
      state.selectedImages = []
    },
    addSelectedImages: (state, { payload }) => {
      state.selectedImages.push(payload)
    },
    removeSelectedImage: (state, { payload }) => {
      const index = state.selectedImages.findIndex(
        ({ id }) => id === payload.id
      )
      if (index !== -1) state.selectedImages.splice(index, 1)
    },
    deleteUploadImage: (state, { payload }) => {
      const index = state.images.findIndex(({ id }) => id === payload.id)
      if (index !== -1) state.images.splice(index, 1)
    },
    setTabIndex: (state, { payload }) => {
      state.tabIndex = payload
      state.name = ''
      state.page = 0
      state.selectedImages = []
    },
    setPage: (state, { payload: page }) => {
      state.page = page
    },
    setSize: (state, { payload: size }) => {
      state.size = size
    },
    setName: (state, { payload: name }) => {
      state.name = name
      state.page = 0
    },
  },

  extraReducers: builder => {
    builder.addCase(uploadImages.pending, (state, { meta: { arg } }) => {
      state.totalProgress = 0
      state.progress = {}
      state.progressCount = arg.length
      state.pendingImages = arg.map(({ width, height, name }) => ({
        width,
        height,
        name,
      }))
      state.uploading = true
    })

    builder.addCase(uploadImages.fulfilled, state => {
      state.totalProgress = 0
      state.progress = {}
      state.progressCount = 0
      state.pendingImages = []
      state.uploading = false
    })

    builder.addCase(uploadImages.rejected, state => {
      state.totalProgress = 0
      state.progress = {}
      state.progressCount = 0
      state.pendingImages = []
      state.uploading = false
    })

    builder.addCase(
      uploadImage.fulfilled,
      (state, { payload, meta: { arg } }) => {
        const index = state.pendingImages.findIndex(
          item => item.name === arg.file.name
        )
        if (index !== -1) state.pendingImages.splice(index, 1)
        const {
          property: { filename, filesize, width, height },
          https: { image },
        } = payload
        const uploadedImage = {
          id: nanoid(),
          url: image,
          name: filename,
          size: filesize,
          width,
          height,
        }
        state.images.push(uploadedImage)
        if (state.selectedImages.length < state.maxCount) {
          state.selectedImages.push(uploadedImage)
        }
      }
    )

    builder.addCase(addImageLibrary.pending, state => {
      state.loading = true
    })

    builder.addCase(addImageLibrary.fulfilled, (state, { payload }) => {
      state.selectedImages = payload.dspImageDtos.map(
        ({ id, url, width, height, name, size }) => ({
          id,
          url,
          width,
          height,
          name,
          size,
        })
      )
      state.loading = false
    })

    builder.addCase(addImageLibrary.rejected, state => {
      state.loading = false
    })

    builder.addCase(fetchModalImageList.pending, state => {
      state.pending = true
    })

    builder.addCase(fetchModalImageList.rejected, state => {
      state.libraryImages = []
      state.totalElements = 0
      state.pending = false
    })

    builder.addCase(fetchModalImageList.fulfilled, (state, { payload }) => {
      state.libraryImages = payload.dspImageDtos.map(({ size, ...rest }) => ({
        ...rest,
        size: Number(size),
      }))
      state.totalElements = payload.totalElements
      state.pending = false
    })

    builder.addCase(
      uploadProgress,
      (state, { payload: { id, progress, filesNum } }) => {
        state.progress[id] = Number(progress)
        state.totalProgress =
          Object.values(state.progress).reduce((acc, curr) => acc + curr, 0) /
          filesNum
      }
    )
  },
})

const { actions, reducer } = imageLibrarySlice
export const {
  showImageUploadModal,
  showImageLibraryModal,
  hideImageLibraryModal,
  addSelectedImages,
  removeSelectedImage,
  setSelectedImages,
  clearSelectedImages,
  deleteUploadImage,
  setTabIndex,
  setPage,
  setSize,
  setName,
} = actions
export default reducer
